import z from 'zod'

import { clinicIdSchema, type WithNullable } from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Button } from '@/frontend/components/ui/button'
import { templateOptions } from '@/frontend/features/schedules/template-options'
import {
  useCreateScheduleOverride,
  useUpdateScheduleOverride,
} from '@/frontend/features/schedules/use-schedule-override-mutations'
import { useScheduleTemplates } from '@/frontend/features/schedules/use-schedule-templates'

const overrideSchema = z.object({
  staffId: z.uuid(),
  date: z.iso.date(),
  clinicId: z.enum(clinicIdSchema.options, { message: 'Please select a clinic' }),
  templateId: z.uuid(),
  note: z.string(),
})

// The schema is the strict, submittable contract; the form's live state loosens
// clinicId to null so the picker's "no selection" binds without a cast.
export type ScheduleOverrideFormValues = WithNullable<z.infer<typeof overrideSchema>, 'clinicId'>

type Props = {
  defaultValues: ScheduleOverrideFormValues
  onSuccess?: () => void
  onCancel?: () => void
  /** When provided (edit mode), renders a Delete button that triggers this callback. */
  onDelete?: () => void
} & ({ overrideId?: undefined } | { overrideId: string })

/** Create/edit form for a per-day schedule override. */
export function ScheduleOverrideForm({
  defaultValues,
  overrideId,
  onSuccess,
  onCancel,
  onDelete,
}: Props) {
  const { data: templates = [] } = useScheduleTemplates()
  const createOverride = useCreateScheduleOverride()
  const updateOverride = useUpdateScheduleOverride()
  const isEditing = overrideId !== undefined

  const templateItems = templateOptions(templates, defaultValues.templateId)

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: overrideSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      // The onSubmit validator already ran, so re-parsing recovers the strict
      // types it guaranteed (notably a non-null clinicId) without a manual guard.
      const parsed = overrideSchema.parse(value)
      try {
        // The mutation hooks own cache invalidation. The PATCH schema doesn't accept
        // `staffId` (a vet can't be reassigned after creation), so the edit payload omits it.
        if (isEditing) {
          await updateOverride.mutateAsync({
            id: overrideId,
            body: {
              date: parsed.date,
              clinicId: parsed.clinicId,
              templateId: parsed.templateId,
              note: parsed.note || null,
            },
          })
        } else {
          await createOverride.mutateAsync({
            staffId: parsed.staffId,
            date: parsed.date,
            clinicId: parsed.clinicId,
            templateId: parsed.templateId,
            note: parsed.note || null,
          })
          form.reset()
        }
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save shift',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <form.AppField name="staffId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Vet"
                disabled={isEditing}
                render={({ id, disabled }) => (
                  <StaffSearch
                    id={id}
                    value={field.state.value}
                    onChange={(staff) => field.handleChange(staff?.id ?? '')}
                    onBlur={field.handleBlur}
                    placeholder="Select a vet"
                    disabled={disabled}
                    aria-invalid={!!field.state.meta.errors.length}
                  />
                )}
              />
            )}
          </form.AppField>

          <form.AppField name="date">
            {(field) => <field.InputField label="Date" type="date" />}
          </form.AppField>

          <form.AppField name="clinicId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Clinic"
                render={({ id, disabled }) => (
                  <ClinicSearch
                    id={id}
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                    placeholder="Select a clinic"
                    aria-invalid={!!field.state.meta.errors.length}
                  />
                )}
              />
            )}
          </form.AppField>

          <form.AppField name="templateId">
            {(field) => (
              <field.SelectField
                label="Schedule template"
                placeholder="Select a template"
                items={templateItems}
              />
            )}
          </form.AppField>

          <form.AppField name="note">
            {(field) => <field.InputField label="Note" placeholder="Optional" />}
          </form.AppField>
        </div>

        <div className="pt-3 flex items-center gap-x-2 justify-end">
          {isEditing && onDelete && (
            <Button
              variant="ghost-destructive"
              size="lg"
              type="button"
              className="mr-auto"
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add shift'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
